<template>
  <template v-if="variant === 'banner'">
    <a
      :href="href"
      data-ta-tag="to-v2-console-banner"
      class="text-primary font-semibold hover:underline"
    >
      {{ t('tryNow') }} &rarr;
    </a>
    <span v-if="fallbackMessage" class="ml-2">{{ fallbackMessage }}</span>
  </template>
  <NbTooltip v-else :text="fallbackMessage" :tooltip-id="tooltipId">
    <a
      :href="href"
      :aria-describedby="fallbackMessage ? tooltipId : undefined"
      data-ta-tag="to-v2-console"
      class="inline-flex items-center gap-1.5 rounded-full border border-yellow-400 bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-900 hover:bg-yellow-200"
    >
      <SparklesIcon class="w-4 h-4" />
      {{ t('tryV2') }}
    </a>
  </NbTooltip>
</template>

<script setup lang="ts">
import { useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { SparklesIcon } from '@heroicons/vue/24/outline'
import NbTooltip from '@/stories/components/NbTooltip.vue'
import type { V2ConsoleTarget } from '@/utils/v2ConsoleTarget'

const props = defineProps<{
  href: string
  variant: 'banner' | 'nav'
  target: V2ConsoleTarget
}>()

const { t } = useI18n()
const tooltipId = `v2-console-fallback-${useId()}`
const fallbackMessage = computed(() => {
  if (props.target.match !== 'fallback') return ''
  return t('fallback', {
    destination: t(`destinations.${props.target.fallbackDestination}`)
  })
})
</script>

<i18n lang="yaml">
en:
  tryNow: Try it now
  tryV2: Try v2 Console
  fallback: This page isn’t available in v2 yet. Opens the {destination}.
  destinations:
    home: Console v2 home
    organization: organization overview
    instance: instance overview
    instanceCreate: instance creation flow
    infrastructure: infrastructure overview
    pulsarCluster: Pulsar cluster overview
    pulsarClusterDetails: Pulsar cluster details
    kafkaCluster: Kafka cluster overview
    kafkaClusterDetails: Kafka cluster details
    workspace: workspace overview
    workspaceConnections: workspace connections settings
    agents: agents list
    agent: agent overview
    tenant: tenant overview
    namespace: namespace overview
    topic: topic overview
    kafkaTopic: Kafka topic overview
    connector: connector overview
    function: function overview
    universalLink: Universal Link overview
    universalLinks: Universal Links list
zh:
  tryNow: 立即体验
  tryV2: 体验 v2 Console
  fallback: 此页面暂未在 v2 中提供，将打开{destination}。
  destinations:
    home: Console v2 首页
    organization: 组织概览页
    instance: Instance 概览页
    instanceCreate: Instance 创建流程
    infrastructure: 基础设施概览页
    pulsarCluster: Pulsar Cluster 概览页
    pulsarClusterDetails: Pulsar Cluster 详情页
    kafkaCluster: Kafka Cluster 概览页
    kafkaClusterDetails: Kafka Cluster 详情页
    workspace: Workspace 概览页
    workspaceConnections: Workspace 连接设置页
    agents: Agent 列表
    agent: Agent 概览页
    tenant: Tenant 概览页
    namespace: Namespace 概览页
    topic: Topic 概览页
    kafkaTopic: Kafka Topic 概览页
    connector: Connector 概览页
    function: Function 概览页
    universalLink: Universal Link 概览页
    universalLinks: Universal Links 列表
</i18n>
